import React, {ReactNode} from 'react';
import {Validated} from './general';
import {SelectProps} from 'antd/lib/select';
import {AddressIPType, AddressType, DisableReasons} from '../utils/enums';

export interface Column<T> extends Validated<T> {
    header: string;
    width: string | number;
    maxLength?: number;
    displayOnEdit?: boolean;
    disableEdit?: (row: T) => boolean;
    mandatory?: boolean | ((row: T) => boolean);
    getDisplayElement?: (column: Column<T>, row: T) => ReactNode;
    getPlaceholder?: (column: Column<T>, row: T) => string;
    type?: EditType;
    options?: (row: T) => { value: string; label: string }[];
    dropdownInfoContent?: (id: string) => React.ReactNode;
    selectProps?: SelectProps;
    isKeyField?: boolean;
    showDuplicatedError?: boolean;
    sticky?: 'first' | 'second';
    description?: string;
    isHidden?: boolean;
    getValue?: (row: T) => any;
    isDescriptionHidden?: boolean;
    subColumns?: Column<T>[];
    parent?: string;
    sub?: boolean;
    isSortable?: boolean;
    sortFn?: (a: any, b: any, direction: 'asc' | 'desc') => number;
}

export enum TableActionType {
    Remove
}

export interface CrudConfig<T> {
    displayInlineActions: boolean;
    rowLimit?: number;
    entityName?: string;
    createNewText: string;
    buttonClass?: string;
    newRecord: Partial<T>;
    actionTooltips?: {
        edit?: string;
        remove?: string;
        className?: string;
        placement?: 'top' | 'left' | 'right' | 'bottom';
    };
    disableEditReason?: DisableReasons | string;
    isDuplicated?: (record: T, originalRecord: T | undefined, keys: string[]) => boolean;
    saveCb?: (newRecord: T) => Promise<void> | void;
    updateCb?: (updatedRecord: T, originalRecord: T) => Promise<void> | void;
    removeCb?: (record: T) => Promise<void> | void;
    disable?: {
        [K in TableActionType]?: (record: T) => boolean;
    };
    dataChangesHandler: ProxyHandler<any>;
    InfoMsg?: React.FC<{ data: T }>;
    getDisableSaveAlert?: (data: T) => string | undefined;
    modalWidth?: number;
    customActions?: (rowData: T) => ReactNode;
    addModalTitle?: string;
    removeModal?: {
        title: string;
        body: (rowData: T) => ReactNode;
        successMsg?: (rowData: T) => string;
    };
}

export enum EditType {
    Dropdown = 'Dropdown',
    CustomDropdown = 'CustomDropdown',
    Text = 'Text',
    TextArea = 'TextArea',
    IP = 'IP',
    RadioButton = 'RadioButton',
}

export function flattenColumns<T>(columns: Column<T>[]): Column<T>[] {
    let flattenedColumns: Column<T>[] = [];
    columns.forEach(column => {
        if (column.subColumns) {
            const subColumnsWithProperties = column.subColumns.map((subColumn, index) => {
                if (index === 0) {
                    return {
                        ...subColumn,
                        sub: true,
                        parent: column.header,
                    };
                } else {
                    return {
                        ...subColumn,
                        sub: true,
                    };
                }
            });
            flattenedColumns = flattenedColumns.concat(flattenColumns(subColumnsWithProperties));
        } else {
            flattenedColumns.push(column);
        }
    });
    return flattenedColumns;
}

export const handleIPTypeChange = (obj: any, prop: string, value: any) => {
    if (prop === 'ipType') {
        obj.value = '';
        if (value === AddressIPType.IP_RANGE) {
            obj.type = AddressType.IPV4_RANGE;
        } else if (value === AddressIPType.IP_NETMASK || value === AddressIPType.IP_SUBNET) {
            obj.type = AddressType.IPV4_NETMASK;
        }
    }
};